import { TransformOptions, UIElement } from "./ui_element.js";

export type CornerRadius =
  | number
  | {
    tl?: number;
    tr?: number;
    br?: number;
    bl?: number;
  };

export type RoundedRectangleFill =
  | string
  | {
    type: "linear-gradient";
    from?: string;
    to?: string;
    stops?: { offset: number; color: string }[];
    direction?: "vertical" | "horizontal";
  };

export type ShadowOptions = {
  color: string;
  blur?: number;
  offsetX?: number;
  offsetY?: number;
  /** CSS order: top, right, bottom, left. Defaults to all sides. */
  sides?: [ boolean, boolean, boolean, boolean ];
  /** Moves individual sides outwards without shifting the whole shadow. */
  sideOffsets?: { top?: number; right?: number; bottom?: number; left?: number };
};

export type InnerShadowOptions = ShadowOptions;

export class RoundedRectangle extends UIElement
{
  private radius: { tl: number; tr: number; br: number; bl: number };
  private color: RoundedRectangleFill;

  private border_color?: string;
  private border_width: number = 0;

  private shadow?: ShadowOptions;
  private inner_shadow?: InnerShadowOptions;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: CornerRadius,
    color: RoundedRectangleFill,
    options?: TransformOptions,
    is_scalable_on_click = false,
    border_color?: string,
    border_width = 0,
    shadow?: ShadowOptions,
    inner_shadow?: InnerShadowOptions
  )
  {
    super( ctx, x, y, width, height, false, is_scalable_on_click, options );

    if ( typeof radius === "number" )
    {
      this.radius = {
        tl: radius,
        tr: radius,
        br: radius,
        bl: radius
      };
    }
    else
    {
      this.radius = {
        tl: radius.tl ?? 0,
        tr: radius.tr ?? 0,
        br: radius.br ?? 0,
        bl: radius.bl ?? 0
      };
    }

    this.color = color;
    this.border_color = border_color;
    this.border_width = border_width;
    this.shadow = shadow;
    this.inner_shadow = inner_shadow;
  }

  public setColor( color: RoundedRectangleFill )
  {
    this.color = color;
    this.forceMakeShouldBeRendered();
  }

  public setBorder( color: string | undefined, width: number ): void
  {
    this.border_color = color;
    this.border_width = width;
    this.forceMakeShouldBeRendered();
  }

  public setWidth( width: number ): void
  {
    this.rect.resize( Math.max( 0, width ), this.rect.getH() );
    this.forceMakeShouldBeRendered();
  }

  private createFillStyle( w2: number, h2: number ): string | CanvasGradient
  {
    if ( typeof this.color === "string" )
      return this.color;

    const gradient = this.color.direction === "horizontal"
      ? this.ctx.createLinearGradient( -w2, 0, w2, 0 )
      : this.ctx.createLinearGradient( 0, -h2, 0, h2 );

    if ( this.color.stops )
    {
      for ( const stop of this.color.stops )
      {
        gradient.addColorStop( stop.offset, stop.color );
      }
    }
    else
    {
      gradient.addColorStop( 0, this.color.from ?? "#000000" );
      gradient.addColorStop( 1, this.color.to ?? "#000000" );
    }

    return gradient;
  }

  private addShadowSideRects(
    w2: number,
    h2: number,
    spread: number,
    sides: [ boolean, boolean, boolean, boolean ],
    inner: boolean
  ): void
  {
    const [ top, right, bottom, left ] = sides;

    if ( inner )
    {
      if ( top ) this.ctx.rect( -w2, -h2, w2 * 2, spread );
      if ( right ) this.ctx.rect( w2 - spread, -h2, spread, h2 * 2 );
      if ( bottom ) this.ctx.rect( -w2, h2 - spread, w2 * 2, spread );
      if ( left ) this.ctx.rect( -w2, -h2, spread, h2 * 2 );
      return;
    }

    if ( top ) this.ctx.rect( -w2 - spread, -h2 - spread, w2 * 2 + spread * 2, spread );
    if ( right ) this.ctx.rect( w2, -h2 - spread, spread, h2 * 2 + spread * 2 );
    if ( bottom ) this.ctx.rect( -w2 - spread, h2, w2 * 2 + spread * 2, spread );
    if ( left ) this.ctx.rect( -w2 - spread, -h2 - spread, spread, h2 * 2 + spread * 2 );
  }

  private dom_last_key: string = "";

  /**
   * An inner shadow on every side is an inset box-shadow: the canvas path clips it to bands
   * 2 x blur + offset wide along the edges, and the shadow fades out well inside them. Without
   * this every such rectangle got its own canvas island (one per leaderboard row).
   */
  private isInnerShadowCss(): boolean
  {
    const inner = this.inner_shadow;
    if ( !inner )
      return true;
    if ( inner.sideOffsets )
      return false;
    return !inner.sides || inner.sides.every( side => side );
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    if ( this.shadow?.sides || !this.isInnerShadowCss() )
      return false;

    const w = this.rect.getW();
    const h = this.rect.getH();
    const clamp = ( r: number ) => Math.min( r, w / 2, h / 2 );
    const radius = `${clamp( this.radius.tl )}px ${clamp( this.radius.tr )}px ${clamp( this.radius.br )}px ${clamp( this.radius.bl )}px`;

    let background: string;
    if ( typeof this.color === "string" )
      background = this.color;
    else
    {
      const stops = this.color.stops
        ? this.color.stops.map( s => `${s.color} ${s.offset * 100}%` )
        : [ `${this.color.from ?? "#000000"} 0%`, `${this.color.to ?? "#000000"} 100%` ];
      background = `linear-gradient(${this.color.direction === "horizontal" ? "to right" : "to bottom"}, ${stops.join( ", " )})`;
    }

    const shadows: string[] = [];
    if ( this.shadow )
      shadows.push( `${this.shadow.offsetX ?? 0}px ${this.shadow.offsetY ?? 0}px ${this.shadow.blur ?? 0}px ${this.shadow.color}` );
    if ( this.inner_shadow )
      shadows.push( `inset ${this.inner_shadow.offsetX ?? 0}px ${this.inner_shadow.offsetY ?? 0}px ${this.inner_shadow.blur ?? 0}px ${this.inner_shadow.color}` );
    if ( this.border_width > 0 && this.border_color )
    {
      const half = this.border_width / 2;
      shadows.push( `0 0 0 ${half}px ${this.border_color}` );
      shadows.push( `inset 0 0 0 ${half}px ${this.border_color}` );
    }

    const key = `${background}|${radius}|${shadows.join( "," )}`;
    if ( key === this.dom_last_key )
      return true;
    this.dom_last_key = key;

    node.style.background = background;
    node.style.borderRadius = radius;
    node.style.boxShadow = shadows.join( ", " );
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();

    const ctx = this.ctx;
    const w = this.rect.getW();
    const h = this.rect.getH();
    const w2 = w / 2;
    const h2 = h / 2;

    const r = {
      tl: Math.min( this.radius.tl, w / 2, h / 2 ),
      tr: Math.min( this.radius.tr, w / 2, h / 2 ),
      br: Math.min( this.radius.br, w / 2, h / 2 ),
      bl: Math.min( this.radius.bl, w / 2, h / 2 ),
    };

    ctx.fillStyle = this.createFillStyle( w2, h2 );

    if ( this.shadow?.sides )
    {
      const side_offsets = this.shadow.sideOffsets;
      const max_side_offset = Math.max(
        side_offsets?.top ?? 0,
        side_offsets?.right ?? 0,
        side_offsets?.bottom ?? 0,
        side_offsets?.left ?? 0
      );
      const spread = ( this.shadow.blur ?? 0 ) * 2
        + Math.abs( this.shadow.offsetX ?? 0 )
        + Math.abs( this.shadow.offsetY ?? 0 ) + max_side_offset + 2;

      const draw_shadow_pass = ( clip_path: () => void, offset_x: number, offset_y: number ) =>
      {
        ctx.save();
        ctx.beginPath();
        clip_path();
        ctx.clip();
        ctx.shadowColor = this.shadow!.color;
        ctx.shadowBlur = this.shadow!.blur ?? 0;
        ctx.shadowOffsetX = offset_x;
        ctx.shadowOffsetY = offset_y;
        ctx.fillStyle = this.createFillStyle( w2, h2 );
        ctx.beginPath();
        ctx.roundRect( -w2, -h2, w, h, [ r.tl, r.tr, r.br, r.bl ] );
        ctx.fill();
        ctx.restore();
      };

      for ( let side = 0; side < 4; side++ )
      {
        if ( !this.shadow.sides[side] )
          continue;

        let offset_x = this.shadow.offsetX ?? 0;
        let offset_y = this.shadow.offsetY ?? 0;
        if ( side === 0 ) offset_y -= side_offsets?.top ?? 0;
        if ( side === 1 ) offset_x += side_offsets?.right ?? 0;
        if ( side === 2 ) offset_y += side_offsets?.bottom ?? 0;
        if ( side === 3 ) offset_x -= side_offsets?.left ?? 0;

        draw_shadow_pass( () =>
        {
          if ( side === 0 ) ctx.rect( -w2 + r.tl, -h2 - spread, w - r.tl - r.tr, spread );
          if ( side === 1 ) ctx.rect( w2, -h2 + r.tr, spread, h - r.tr - r.br );
          if ( side === 2 ) ctx.rect( -w2 + r.bl, h2, w - r.bl - r.br, spread );
          if ( side === 3 ) ctx.rect( -w2 - spread, -h2 + r.tl, spread, h - r.tl - r.bl );
        }, offset_x, offset_y );
      }

      const base_x = this.shadow.offsetX ?? 0;
      const base_y = this.shadow.offsetY ?? 0;
      const draw_corner = (
        enabled: boolean,
        x: number,
        y: number,
        size: number,
        offset_x: number,
        offset_y: number
      ) =>
      {
        if ( enabled )
          draw_shadow_pass( () => ctx.rect( x, y, size, size ), offset_x, offset_y );
      };

      draw_corner(
        this.shadow.sides[0] || this.shadow.sides[1],
        w2 - r.tr, -h2 - spread, r.tr + spread,
        base_x + ( this.shadow.sides[1] ? side_offsets?.right ?? 0 : 0 ),
        base_y - ( this.shadow.sides[0] ? side_offsets?.top ?? 0 : 0 )
      );
      draw_corner(
        this.shadow.sides[1] || this.shadow.sides[2],
        w2 - r.br, h2 - r.br, r.br + spread,
        base_x + ( this.shadow.sides[1] ? side_offsets?.right ?? 0 : 0 ),
        base_y + ( this.shadow.sides[2] ? side_offsets?.bottom ?? 0 : 0 )
      );
      draw_corner(
        this.shadow.sides[2] || this.shadow.sides[3],
        -w2 - spread, h2 - r.bl, r.bl + spread,
        base_x - ( this.shadow.sides[3] ? side_offsets?.left ?? 0 : 0 ),
        base_y + ( this.shadow.sides[2] ? side_offsets?.bottom ?? 0 : 0 )
      );
      draw_corner(
        this.shadow.sides[3] || this.shadow.sides[0],
        -w2 - spread, -h2 - spread, r.tl + spread,
        base_x - ( this.shadow.sides[3] ? side_offsets?.left ?? 0 : 0 ),
        base_y - ( this.shadow.sides[0] ? side_offsets?.top ?? 0 : 0 )
      );
    }

    if ( this.shadow && !this.shadow.sides )
    {
      ctx.shadowColor = this.shadow.color;
      ctx.shadowBlur = this.shadow.blur ?? 0;
      ctx.shadowOffsetX = this.shadow.offsetX ?? 0;
      ctx.shadowOffsetY = this.shadow.offsetY ?? 0;
    }

    ctx.beginPath();

    ctx.moveTo( -w2 + r.tl, -h2 );
    ctx.lineTo( w2 - r.tr, -h2 );
    ctx.arcTo( w2, -h2, w2, -h2 + r.tr, r.tr );

    ctx.lineTo( w2, h2 - r.br );
    ctx.arcTo( w2, h2, w2 - r.br, h2, r.br );

    ctx.lineTo( -w2 + r.bl, h2 );
    ctx.arcTo( -w2, h2, -w2, h2 - r.bl, r.bl );

    ctx.lineTo( -w2, -h2 + r.tl );
    ctx.arcTo( -w2, -h2, -w2 + r.tl, -h2, r.tl );

    ctx.closePath();
    ctx.fill();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    if ( this.inner_shadow )
    {
      ctx.save();
      ctx.clip();
      if ( this.inner_shadow.sides )
      {
        const spread = ( this.inner_shadow.blur ?? 0 ) * 2
          + Math.abs( this.inner_shadow.offsetX ?? 0 )
          + Math.abs( this.inner_shadow.offsetY ?? 0 ) + 2;
        ctx.beginPath();
        this.addShadowSideRects( w2, h2, spread, this.inner_shadow.sides, true );
        ctx.clip();
      }
      ctx.shadowColor = this.inner_shadow.color;
      ctx.shadowBlur = this.inner_shadow.blur ?? 0;
      ctx.shadowOffsetX = this.inner_shadow.offsetX ?? 0;
      ctx.shadowOffsetY = this.inner_shadow.offsetY ?? 0;
      ctx.fillStyle = this.inner_shadow.color;

      ctx.beginPath();
      const spread = Math.max( w, h ) * 2;
      ctx.rect( -spread, -spread, spread * 2, spread * 2 );
      ctx.moveTo( -w2 + r.tl, -h2 );
      ctx.lineTo( w2 - r.tr, -h2 );
      ctx.arcTo( w2, -h2, w2, -h2 + r.tr, r.tr );
      ctx.lineTo( w2, h2 - r.br );
      ctx.arcTo( w2, h2, w2 - r.br, h2, r.br );
      ctx.lineTo( -w2 + r.bl, h2 );
      ctx.arcTo( -w2, h2, -w2, h2 - r.bl, r.bl );
      ctx.lineTo( -w2, -h2 + r.tl );
      ctx.arcTo( -w2, -h2, -w2 + r.tl, -h2, r.tl );
      ctx.closePath();
      ctx.fill( "evenodd" );
      ctx.restore();
    }

    if ( this.border_width > 0 && this.border_color )
    {
      ctx.lineWidth = this.border_width;
      ctx.strokeStyle = this.border_color;
      ctx.stroke();
    }

    this.finishRender();
  }

  public name(): string
  {
    return "RoundedRectangle";
  }
}
